import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { canAccess } from "../hooks/usePermission";
import "./ClientDetail.css";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.quote.softvolute.in";

// Fields that need the full row width
const fullWidthFields = ["clientAddress", "siteAddress"];

const ClientDetail = ({ selectedProject }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [salesManagers, setSalesManagers] = useState([]);
  const [assignedSalesManager, setAssignedSalesManager] = useState("");

  const [clientDetails, setClientDetails] = useState({
    ClientName: "",
    clientAddress: "",
    phoneNumber: "",
    email: "",
    dcId: "",
    possessionDate: "",
    homeType: "",
    panCardNumber: "",
    gstNumber: "",
    siteAddress: "",
    expectedCompletionDate: "",
    projectStartDate: "",
    clientBudget: "",
  });

  const canViewAll = canAccess("clientDetail", "viewAll");

  const canView =
    canAccess("clientDetail", "view") ||
    canViewAll ||
    canAccess("clientDetail", "clientDetailView");

  const canEdit =
    canAccess("clientDetail", "edit") ||
    canAccess("clientDetail", "clientDetailEdit");

  const canSave =
    canAccess("clientDetail", "create") ||
    canAccess("clientDetail", "clientDetailSave");

  const hasFieldPermission = (permissionKey) => {
    return canViewAll || canAccess("clientDetail", permissionKey);
  };

  const canShowClientName = hasFieldPermission("clientName");
  const canShowClientAddress = hasFieldPermission("clientAddress");
  const canShowPhone = hasFieldPermission("phoneNumber");
  const canShowEmail = hasFieldPermission("email");
  const canShowDcId = hasFieldPermission("dcId");
  const canShowPossessionDate = hasFieldPermission("possessionDate");
  const canShowSiteAddress = hasFieldPermission("siteAddress");
  const canShowProjectStartDate = hasFieldPermission("projectStartDate");
  const canShowExpectedCompletionDate = hasFieldPermission(
    "expectedCompletionDate",
  );
  const canShowHomeType = hasFieldPermission("homeType");
  const canShowBudget = hasFieldPermission("clientDetailShowBudget");
  const canShowPAN = hasFieldPermission("clientDetailShowPAN");
  const canShowGST = hasFieldPermission("clientDetailShowGST");
  const canAssignSalesManager = hasFieldPermission(
    "clientDetailAssignSalesManager",
  );

  const allFields = [
    ...(canShowClientName ? ["ClientName"] : []),
    ...(canShowClientAddress ? ["clientAddress"] : []),
    ...(canShowPhone ? ["phoneNumber"] : []),
    ...(canShowEmail ? ["email"] : []),
    ...(canShowDcId ? ["dcId"] : []),
    ...(canShowPossessionDate ? ["possessionDate"] : []),
    ...(canShowPAN ? ["panCardNumber"] : []),
    ...(canShowGST ? ["gstNumber"] : []),
    ...(canShowSiteAddress ? ["siteAddress"] : []),
    ...(canShowProjectStartDate ? ["projectStartDate"] : []),
    ...(canShowExpectedCompletionDate ? ["expectedCompletionDate"] : []),
    ...(canShowBudget ? ["clientBudget"] : []),
  ];

  useEffect(() => {
    if (!selectedProject?.name) {
      setLoading(false);
      return;
    }
    setClientDetails((prev) => ({
      ...prev,
      ClientName: prev.ClientName || selectedProject.name,
    }));
    const fetchClientDetails = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/client-details/${encodeURIComponent(
            selectedProject.name,
          )}`,
        );

        if (res.data?.clientDetails) {
          setClientDetails((prev) => ({
            ...prev,
            ...res.data.clientDetails,
            ClientName:
              res.data.clientDetails.ClientName || selectedProject.name,
          }));
        }

        if (res.data?.assignedSalesManager) {
          setAssignedSalesManager(res.data.assignedSalesManager);
        }
      } catch (err) {
        console.warn("ℹ No existing client details found");
      } finally {
        setLoading(false);
      }
    };

    fetchClientDetails();
  }, [selectedProject]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/users?role=sales_manager`)
      .then((res) => setSalesManagers(res.data || []))
      .catch((err) => console.error("❌ Sales manager fetch error:", err));
  }, []);

  const handleSaveClientDetails = async () => {
    if (!canSave) {
      alert("You do not have permission to save client details");
      return;
    }

    if (!selectedProject?.name) {
      alert("Project not selected");
      return;
    }

    try {
      const res = await axios.post(`${BASE_URL}/api/client-details/save`, {
        projectName: selectedProject.name,
        clientDetails,
        assignedSalesManager,
      });

      if (res.data?.clientDetails) {
        setClientDetails((prev) => ({
          ...prev,
          ...res.data.clientDetails,
        }));
      }

      if (res.data?.assignedSalesManager) {
        setAssignedSalesManager(res.data.assignedSalesManager);
      }

      alert("✅ Client details saved successfully");
    } catch (err) {
      console.error(
        "❌ Error saving client details:",
        err.response?.data || err,
      );
      alert(err.response?.data?.error || "❌ Failed to save client details!");
    }
  };

  const getLabel = (field) => {
    if (field === "ClientName") return "Client Name";
    if (field === "clientAddress") return "Client Address";
    if (field === "phoneNumber") return "Phone Number";
    if (field === "email") return "Email";
    if (field === "dcId") return "DC ID";
    if (field === "possessionDate") return "Possession Date";
    if (field === "panCardNumber") return "PAN Card Number";
    if (field === "gstNumber") return "GST Number";
    if (field === "siteAddress") return "Site Address";
    if (field === "projectStartDate") return "Project Start Date";
    if (field === "expectedCompletionDate") return "Expected Completion Date";
    if (field === "clientBudget") return "Client Budget (₹)";
    return field.replace(/([A-Z])/g, " $1");
  };

  const getInputType = (field) => {
    if (field === "clientBudget") return "number";
    if (field === "phoneNumber") return "tel";
    if (field === "email") return "email";
    if (field.toLowerCase().includes("date")) return "date";
    return "text";
  };

  if (!canView) {
    return (
      <div className="cd-page">
        <div className="cd-header">
          <button
            type="button"
            className="cd-back"
            onClick={() => navigate("/project")}
          >
            ← Back
          </button>
          <h2 className="cd-title">Access Denied</h2>
          <div className="cd-header-spacer" />
        </div>

        <p className="cd-denied">
          You do not have permission to view client details.
        </p>
      </div>
    );
  }

  return (
    <div className="cd-page">
      <div className="cd-header">
        <button
          type="button"
          className="cd-back"
          onClick={() => navigate("/project")}
        >
          ← Back
        </button>
        <h2 className="cd-title">Client Details</h2>
        <div className="cd-header-spacer" />
      </div>

      {loading ? (
        <p className="cd-loading">⏳ Loading client details...</p>
      ) : (
        <form className="cd-form" onSubmit={(e) => e.preventDefault()}>
          {allFields.map((field) => (
            <div
              key={field}
              className={`cd-group${fullWidthFields.includes(field) ? " is-full" : ""}`}
            >
              <label className="cd-label">{getLabel(field)}</label>

              <input
                className="cd-input"
                type={getInputType(field)}
                inputMode={field === "clientBudget" ? "numeric" : undefined}
                value={clientDetails[field] || ""}
                disabled={!canEdit}
                onChange={(e) =>
                  setClientDetails({
                    ...clientDetails,
                    [field]: e.target.value,
                  })
                }
                placeholder={
                  field === "clientBudget" ? "Enter budget in INR" : ""
                }
              />
            </div>
          ))}

          {canShowHomeType && (
            <div className="cd-group">
              <label className="cd-label">Home Type</label>
              <select
                className="cd-input"
                value={clientDetails.homeType}
                disabled={!canEdit}
                onChange={(e) =>
                  setClientDetails({
                    ...clientDetails,
                    homeType: e.target.value,
                  })
                }
              >
                <option value="">-- Select --</option>
                <option value="1BHK">1BHK</option>
                <option value="2BHK">2BHK</option>
                <option value="3BHK">3BHK</option>
                <option value="4BHK">4BHK</option>
                <option value="5BHK">5BHK</option>
                <option value="Bunglow">Bunglow</option>
              </select>
            </div>
          )}

          {canAssignSalesManager && (
            <div className="cd-group">
              <label className="cd-label">Assign Sales Manager</label>
              <select
                className="cd-input"
                value={assignedSalesManager}
                disabled={!canEdit}
                onChange={(e) => setAssignedSalesManager(e.target.value)}
              >
                <option value="">-- Select Sales Manager --</option>
                {salesManagers.map((sm) => (
                  <option key={sm._id} value={sm._id}>
                    {sm.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {canSave && (
            <button
              type="button"
              className="cd-save"
              onClick={handleSaveClientDetails}
            >
              💾 Save Client Details
            </button>
          )}
        </form>
      )}
    </div>
  );
};

export default ClientDetail;
